import {ISocketListener, Socket} from '@mintdata-wsp/common/controllers/network/socket/Socket';
import {SocketConfig} from '@mintdata-wsp/common/controllers/network/socket/SocketConfig';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';

export class WebSocketRequestMessage
{
    private id:string;

    private method:string;

    private body:any;

    constructor(id:string, method:string, body:any)
    {
        this.id = id;
        this.method = method;
        this.body = body;
    }
}

export interface WebSocketResponseMessage
{
    requestId:string;
    method:string;
    status:number;
    body:any;
    timestamp:number;
}

export type WebSocketRequest = any;

export type ErrorResponse = WebSocketResponseMessage;
export type WebSocketResponse = any | ErrorResponse;

export type ResponseConstructor = new(json:any) => any;

interface ResponseDeferred
{
    socket:Socket,
    responseConstructor:ResponseConstructor,
    resolve:(value?:WebSocketResponse | PromiseLike<WebSocketResponse>) => void,
    reject:(reason?:any) => void
}

export type EventHandler = (body:WebSocketResponse) => void;

export const DEFAULT_MAX_OPEN_SOCKETS = 5;

// class name represents the fact, that it's a client-counterpart for rest-engine's server-side websockets
export class RestEngineSocketClient
{
    private _config:SocketConfig;

    private _sockets:Socket[] = [];

    private _maxOpenSockets:number = 1;

    private _autoReconnect:boolean = true;

    private _socketListeners:ISocketListener[] = [];

    private _lastSocketIndex = -1;

    private _isConnected:boolean = true;

    private _responseDeffereds:Map<string, ResponseDeferred> = new Map();

    private _eventHandlers:Map<string, EventHandler[]> = new Map();

    constructor(config:SocketConfig, maxOpenSockets:number = DEFAULT_MAX_OPEN_SOCKETS, autoReconnect:boolean = true)
    {
        this._config = config;
        this._maxOpenSockets = maxOpenSockets;
        this._autoReconnect = autoReconnect;
    }

    public addSocketListener(listener:ISocketListener):void
    {
        this._socketListeners.push(listener);
        this._sockets.forEach(socket => socket.addListener(listener));
    }

    public removeSocketListener(listener:ISocketListener):void
    {
        this._socketListeners.push(listener);
        this._sockets.forEach(socket => socket.removeListener(listener));
    }

    public async connect():Promise<void>
    {
        return new Promise<void>((resolve, reject) =>
        {
            const socket = this._chooseSocket();
            const listener = {
                onOpen: (event) =>
                {
                    resolve(null);
                    socket.removeListener(listener);
                },
                onError: (event) =>
                {
                    reject('Can\'t connect to remote server');
                    socket.removeListener(listener);
                },
                onClose: (event) =>
                {
                    reject('Can\'t connect to remote server');
                    socket.removeListener(listener);
                }
            };
            socket.addListener(listener);
        });
    }

    public async send(method:string,
                      body:WebSocketRequest = null):Promise<void>
    {
        return this.request(method, null, body);
    }

    public async request(method:string,
                         responseConstructor:ResponseConstructor,
                         body:WebSocketRequest = null):Promise<WebSocketResponse>
    {
        if( !this.isConnected() )
            return Promise.reject('Socket Client is disсonnected');

        return new Promise<WebSocketResponse>((resolve, reject) =>
        {
            const requestId:string = IdUtil.newGUID();

            const request = new WebSocketRequestMessage(requestId, method, body);

            const socket = this._chooseSocket();

            socket.sendMessage(
                JSON.stringify(request)
            );

            if( responseConstructor != null )
                this._responseDeffereds.set(requestId, {socket, resolve, reject, responseConstructor});
            else
                resolve(null);
        });
    }

    public _chooseSocket():Socket
    {
        this._prepareSockets();

        this._lastSocketIndex = (this._lastSocketIndex + 1) % this._sockets.length;

        return this._sockets[this._lastSocketIndex];
    }

    private _prepareSockets()
    {
        const currentRequestsCnt = this._responseDeffereds;
        const currentSocketsCnt = this._sockets.length;
        if( currentRequestsCnt.size >= currentSocketsCnt && currentSocketsCnt < this._maxOpenSockets )
        {
            const socket = new Socket(this._config.connectionString);
            socket.setMaxConnectionRetries(this._autoReconnect ? null : -1);
            socket.addListener({
                onMessage: (event) => this._onMessage(socket, event),
                onClose: (event) => this._closeSocket(socket)
            });
            this._socketListeners.forEach(listener => socket.addListener(listener));

            this._sockets.push(socket);
        }
    }

    public subscribe(method:string, onEvent:EventHandler)
    {
        if( !this._eventHandlers.has(method) )
            this._eventHandlers.set(method, []);

        this._eventHandlers.get(method).push(onEvent);
    }

    public unsubscribe(method:string, onEvent:EventHandler):boolean
    {
        const eventHandlers = this._eventHandlers.get(method);
        if( eventHandlers )
        {
            const deleteIndex = eventHandlers.indexOf(onEvent);
            if( deleteIndex >= 0 )
            {
                eventHandlers.splice(deleteIndex, 1);
                return true;
            }
        }

        return false;
    }

    public isConnected():boolean
    {
        return this._isConnected;
    }

    public disconnect():void
    {
        this._isConnected = false;

        //iterate on a copy, because _sockets array will be changed inside forEach below
        [...this._sockets].forEach(socket => this._closeSocket(socket));

        this._sockets = [];
    }

    @bind
    private _onMessage(socket:Socket, event:MessageEvent):void
    {
        const response = JSON.parse(event.data) as WebSocketResponseMessage;

        const requestId = response.requestId;
        const method = response.method;

        if( requestId )
        {
            // response message...
            const responseDeferred = this._responseDeffereds.get(requestId);
            if( !responseDeferred )
            {
                console.error(`Got a response for unknown request with id: ${requestId}`);
                return;
            }

            if( response.status !== 200 )
                responseDeferred.reject(response);
            else
                responseDeferred.resolve(response.body);

            this._responseDeffereds.delete(requestId);
        }

        const eventHandlers = this._eventHandlers.get(method) || [];
        for( let eventHandler of eventHandlers )
        {
            try
            {
                eventHandler(response.body);
            }
            catch( e )
            {
                console.error('Error while executing event handler: ', e);
            }
        }
    }

    @bind
    private _closeSocket(socket:Socket):void
    {
        socket.close();

        // fail all ongoing requests on that socket ...
        const toDelete = [];
        this._responseDeffereds.forEach((responseDeferred, requestId) =>
        {
            if( responseDeferred.socket === socket )
            {
                responseDeferred.reject('Connection closed');
                toDelete.push(requestId);
            }
        });

        toDelete.forEach(requestId => this._responseDeffereds.delete(requestId));
        this._sockets = this._sockets.filter(s => s !== socket);
    }
}
